import { useState, Dispatch, useEffect } from 'react';
import { Wrapper } from '../Common';
import { useMutation } from 'react-query';
import {
  getPreviousQuestionsValue,
  currencyFormat,
} from 'src/common/utils/functions';
import { submitRiskAssessment } from './SendAnswersMutation';
import ActionBar from './ActionBar';
import QuestionTypeFactory from 'src/common/components/QuestionTypeFactory';
import { DisplayState, ErrorObject, DEFAULT_ERROR_STATE } from '../types';
import styled from 'styled-components';
import ToastAlert from 'src/common/components/ToastAlert';
import { mobileBreakpoint } from '../mobileBreakpoint';
import { getQuestionFromAllQuestions, includeQuestions } from './utils';
import { useQueryParamsWithIds } from 'src/common/hooks/useQueryParamsWithIds';

const Div = styled.div`
  width: 100%;
`;

const H1 = styled.h1`
  color: #101828;
  font: 500 1.75rem/2.125rem Inter, sans-serif;
  width: 100%;
  @media ${mobileBreakpoint.mobile} {
    font-size: 20px;
  }
`;

const Span = styled.span`
  color: #101828;
  font: 500 0.75rem/1.5rem Inter, sans-serif;
`;

type Props = {
  clientName?: string;
  questions: any[];
  questionnaireValue: any;
  newQuestions: any[];
  setNewQuestions: Dispatch<any[]>;
  setQuestionnaireValue: Dispatch<any>;
  setDisplayState: Dispatch<DisplayState>;
};

const getQuestionLabel = (
  label: string,
  attributeKey: string,
  questionnaireValue: any,
  questions: any[],
) => {
  if (
    ['investment_loss_preference', 'investment_gain_preference'].includes(
      attributeKey,
    )
  ) {
    const portfolioValue = getPreviousQuestionsValue(
      'investment_amount',
      questions,
      questionnaireValue,
    );
    return `For your $${currencyFormat(
      portfolioValue,
    )}, which of the following best describes you?`;
  } else if (attributeKey === 'geo_zip_code_owner') {
    const zipCodeValue = getPreviousQuestionsValue(
      'address',
      questions,
      questionnaireValue,
    )?.zipcode;
    return `Do you own property in the ${zipCodeValue} Zip Code?`;
  }
  return label;
};

const TARGET_WEALTH_TEXT =
  'Target wealth should be greater than Starting Wealth.';

function QuestionMode({
  clientName = 'Iain Gillespie',
  newQuestions,
  questions = [],
  questionnaireValue,
  setNewQuestions,
  setQuestionnaireValue,
  setDisplayState,
}: Props) {
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(0);
  const [errorDialog, openErrorDialog] = useState({ show: false, message: '' });
  const [error, setError] = useState<ErrorObject>(DEFAULT_ERROR_STATE);
  const { isLoading, mutate, isError } = useMutation(submitRiskAssessment);
  const { clientId } = useQueryParamsWithIds();
  const [marriedQuestionId, setMarriedQuestionId] = useState<string>('');
  const [dependentsQuestionId, setDependentsQuestionId] = useState<string>('');
  const [dependentChildrenId, setDependentChildrenId] = useState<string>('');
  const [childrenQuestionId, setChildrenQuestionId] = useState<string>('');
  const [ownHouseQuestionId, setOwnHouseQuestionId] = useState<string>('');

  useEffect(() => {
    if (questions.length > 0) {
      const findingMarriedQuestion = questions.find(({ attributeKey }) =>
        ['married', 'include_spouse'].includes(attributeKey),
      );
      const findingDependentsQuestion = questions.find(({ attributeKey }) =>
        ['household_body_count'].includes(attributeKey),
      );

      const findingChildrenQuestion = questions.find(({ attributeKey }) =>
        ['children_bool', 'num_children'].includes(attributeKey),
      );
      const findingDependentChildrenQuestion = questions.find(
        ({ attributeKey }) => ['dependant_children'].includes(attributeKey),
      );
      const findingOwnHouseQuestion = questions.find(({ attributeKey }) =>
        ['owns_house'].includes(attributeKey),
      );

      setDependentsQuestionId(
        findingDependentsQuestion ? findingDependentsQuestion.questionId : '',
      );
      setMarriedQuestionId(
        findingMarriedQuestion ? findingMarriedQuestion.questionId : '',
      );
      setChildrenQuestionId(
        findingChildrenQuestion ? findingChildrenQuestion.questionId : '',
      );
      setDependentChildrenId(
        findingDependentChildrenQuestion
          ? findingDependentChildrenQuestion?.questionId
          : '',
      );
      setOwnHouseQuestionId(
        findingOwnHouseQuestion ? findingOwnHouseQuestion?.questionId : '',
      );
      setNewQuestions(questions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions]);

  useEffect(() => {
    const excludeTheseQuestions = [
      'geo_zip_code',
      'household_income',
      'city',
      'state',
    ];

    if (ownHouseQuestionId && questionnaireValue[ownHouseQuestionId]) {
      excludeTheseQuestions.push('monthly_rent_expense');
    }
    if (
      dependentChildrenId &&
      questionnaireValue[dependentChildrenId ?? ''] === 0
    ) {
      excludeTheseQuestions.push(
        'dependent_children_ages',
        'children_in_college',
      );
    }
    if (
      childrenQuestionId !== '' &&
      questionnaireValue[childrenQuestionId] === false
    )
      excludeTheseQuestions.push('num_children');
    if (
      marriedQuestionId !== '' &&
      questionnaireValue &&
      questionnaireValue[marriedQuestionId] === false
    ) {
      const marriedDependentQuestions = [
        'spouse_age',
        'spouse_retirement_age',
        'spouse_income',
        'spouse_retirement_acnt',
        'sps_investment_balance',
        'sps_employer_contrib',
      ];
      if (
        dependentsQuestionId !== '' &&
        questionnaireValue[dependentsQuestionId] === 1
      ) {
        marriedDependentQuestions.push('household_body_count_under_18');
      }

      if (questions.length) {
        setNewQuestions(
          questions.filter(
            ({ attributeKey }) =>
              !marriedDependentQuestions
                .concat(excludeTheseQuestions)
                .includes(attributeKey),
          ),
        );
      }
    } else if (
      dependentsQuestionId !== '' &&
      questionnaireValue &&
      questionnaireValue[dependentsQuestionId] === 1
    ) {
      setNewQuestions(
        questions.filter(
          ({ attributeKey }) =>
            !['household_body_count_under_18']
              .concat(excludeTheseQuestions)
              .includes(attributeKey),
        ),
      );
    } else {
      const filteredQuestions = questions.filter(
        ({ attributeKey }) => !excludeTheseQuestions.includes(attributeKey),
      );
      if (filteredQuestions.length !== newQuestions.length) {
        setNewQuestions(filteredQuestions);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaireValue]);

  const handleError = (err: any) => {
    const responseErrors = err?.response?.errors;
    if (responseErrors.length) {
      const parsedError = JSON.parse(responseErrors[0].message);
      if (parsedError.length) {
        const errResponses = parsedError.reduce((mainError: any, item: any) => {
          const data = item.response.errors;
          if (data.length) {
            return mainError.concat(data[0].message);
          }
        }, []);
        if (
          errResponses.length &&
          errResponses.some((item: string) => item.includes(TARGET_WEALTH_TEXT))
        ) {
          openErrorDialog({ message: TARGET_WEALTH_TEXT, show: true });
        } else {
          openErrorDialog({
            message: 'Error occurred while processing request.',
            show: true,
          });
        }
      } else {
        openErrorDialog({
          message: 'Error occurred while processing request.',
          show: true,
        });
      }
    } else {
      openErrorDialog({
        message: 'Error occurred while processing request.',
        show: true,
      });
    }
  };

  const handleChangeSteps = (type: 'NEXT' | 'PREV') => {
    if (type === 'NEXT') {
      if (selectedQuestionIndex + 1 !== newQuestions.length)
        setSelectedQuestionIndex(selectedQuestionIndex + 1);
      else {
        const addressQuestion = newQuestions.find(
          ({ attributeKey }) => attributeKey === 'address',
        );
        const excludeThisQuestion = newQuestions.find(
          ({ attributeKey }) => attributeKey === 'tap_these_causes',
        );

        let newQuestionnaireValue = questionnaireValue;
        if (excludeThisQuestion) {
          const qid = excludeThisQuestion.questionId;
          let { [qid]: newId, ...rest } = questionnaireValue;
          newQuestionnaireValue = rest;
        }

        const selectedIncludeQuestions = includeQuestions(
          questions,
          newQuestions,
        );

        const values = selectedIncludeQuestions.reduce(
          (acc, currentQuestion) => {
            const {
              questions,
              attributeKey,
              referenceAttributeKey = '',
              defaultValue = 0,
            } = currentQuestion;

            const value = getQuestionFromAllQuestions(
              questions,
              attributeKey,
              questionnaireValue,
              referenceAttributeKey,
              attributeKey,
              defaultValue,
            );

            if (value && Object.keys(value).length) {
              return { ...acc, ...value };
            }
            return acc;
          },
          newQuestionnaireValue,
        );

        if (addressQuestion) {
          const questionIdForAddress = addressQuestion.questionId;
          const addressQuestionValue = values[questionIdForAddress];
          mutate(
            {
              answers: {
                ...values,
                [questionIdForAddress]: `${addressQuestionValue.city ?? ``},${
                  addressQuestionValue.state?.value ?? ''
                },${addressQuestionValue.zipcode ?? ''}`,
              },
              clientId,
            },
            {
              onSuccess: res => {
                setDisplayState('CONCLUSION');
              },
              onError: err => {
                handleError(err);
              },
            },
          );
        } else {
          mutate(
            {
              answers: values,
              clientId,
            },
            {
              onSuccess: res => {
                setDisplayState('CONCLUSION');
              },
              onError: err => {
                handleError(err);
              },
            },
          );
        }
      }
    } else {
      if (selectedQuestionIndex > 0)
        setSelectedQuestionIndex(selectedQuestionIndex - 1);
      else setDisplayState('INTRO');
    }
  };
  if (newQuestions.length === 0) return <></>;

  return (
    <>
      <Wrapper width="100%" alignItems="center">
        <Div>
          <H1>
            {getQuestionLabel(
              newQuestions[selectedQuestionIndex]?.label,
              newQuestions[selectedQuestionIndex]?.attributeKey,
              questionnaireValue,
              questions,
            )}
          </H1>
          <Span
            style={{
              color: '#475467',
              fontSize: '14px',
              fontWeight: '400',
              marginBottom: '20px',
              marginTop: '20px',
              display: 'block',
            }}
          >
            {newQuestions[selectedQuestionIndex]?.subText ?? ''}
          </Span>
        </Div>
        <QuestionTypeFactory
          selectedIndex={selectedQuestionIndex}
          selectedQuestionId={
            newQuestions.length > 0
              ? newQuestions[selectedQuestionIndex]?.questionId
              : ''
          }
          questions={newQuestions}
          totalLength={newQuestions.length}
          questionItem={newQuestions[selectedQuestionIndex]}
          questionnaireValue={questionnaireValue}
          setQuestionnaireValue={setQuestionnaireValue}
          error={error}
          setError={setError}
        />
        {/* <HelperText /> */}
        <ActionBar
          questions={newQuestions}
          selectedQuestionId={
            newQuestions.length > 0
              ? newQuestions[selectedQuestionIndex].questionId
              : ''
          }
          currentQuestion={selectedQuestionIndex}
          handleChangeStep={handleChangeSteps}
          questionnaireValue={questionnaireValue}
          setQuestionnaireValue={setQuestionnaireValue}
          isLoading={isLoading}
          error={error}
          setError={setError}
        />
      </Wrapper>
      {isError && (
        <ToastAlert
          message={errorDialog.message}
          open={errorDialog.show}
          toggleOpen={(x: boolean) => openErrorDialog({ show: x, message: '' })}
        />
      )}
    </>
  );
}

export default QuestionMode;
